/**
 * @license
 * Copyright 2021 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */

import { css, html, LitElement } from "lit";
import { customElement } from "lit/decorators.js";

@customElement("slide-button")
class SlideButton extends LitElement {
  static override styles = css`
    #btn {
      width: 48px;
      height: 48px;
      cursor: pointer;
      border-radius: 24px;

      display: flex;
      align-items: center;
      justify-content: center;

      background: linear-gradient(145deg, 
        var(--affine-background-primary-color), 
        rgba(var(--affine-primary-color-rgb, 140, 92, 245), 0.05));
      border: 1px solid rgba(var(--affine-primary-color-rgb, 140, 92, 245), 0.1);
      
      box-shadow: 
        0 4px 16px rgba(0, 0, 0, 0.08),
        0 2px 4px rgba(0, 0, 0, 0.04),
        inset 0 1px 0 rgba(255, 255, 255, 0.1);
      
      transition: all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1);
      position: relative;
      overflow: hidden;
    }

    #btn::before {
      content: '';
      position: absolute;
      inset: 0;
      background: linear-gradient(135deg, 
        rgba(var(--affine-primary-color-rgb, 140, 92, 245), 0.1), 
        transparent);
      opacity: 0;
      transition: opacity 0.2s ease;
    }

    #btn:hover {
      transform: translateY(-1px);
      box-shadow: 
        0 8px 24px rgba(0, 0, 0, 0.12),
        0 4px 8px rgba(0, 0, 0, 0.08),
        inset 0 1px 0 rgba(255, 255, 255, 0.2);
      border-color: rgba(var(--affine-primary-color-rgb, 140, 92, 245), 0.2);
    }

    #btn:hover::before {
      opacity: 1;
    }

    #btn:active {
      transform: translateY(0) scale(0.95);
      box-shadow: 
        0 2px 8px rgba(0, 0, 0, 0.15),
        inset 0 2px 4px rgba(0, 0, 0, 0.05);
    }

    ::slotted(svg) {
      width: 20px;
      height: 20px;
      color: var(--affine-text-primary-color);
      opacity: 0.8;
      transition: all 0.2s ease;
      z-index: 1;
      position: relative;
    }

    #btn:hover ::slotted(svg) {
      color: var(--affine-primary-color);
      opacity: 1;
    }
  `;

  override render() {
    return html`<div
      part="internal-btn"
      id="btn"
      tabindex="0"
      role="button"
      aria-pressed="false"
    >
      <slot></slot>
    </div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "slide-button": SlideButton;
  }
} 